import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trackEvent } from 'utils/analytics';
import { SpotInterface } from 'types/Spot';
import { DocumentSubCategory, DocumentUploadCategory, PropertyDocument, SUBCATEGORY_LABELS } from 'types/Documents';
import FolderIcon from 'assets/icons/folder.svg?react';
import { getDocumentsByCategory, getDocumentCategories } from 'utils/documents';
import Button from 'components/Button/Button';
import Modal, { ModalRef } from 'components/Modal/Modal';
import HistoricalFinancialItem from './ItemByCategory/HistoricalFinancial';
import MonthlyRentRollItem from './ItemByCategory/MonthlyRentRoll';
import MapAndBluePrintItem from './ItemByCategory/MapAndBluePrint';
import EntryExitPhotosItem from './ItemByCategory/EntryExitPhotos';
import { BlueprintInfo, EntryExitInfo, SecurityInfo } from './utils';
import styles from './NewOwnerOnboarding.module.css';

type OnboardingBannerProps = {
    documents: PropertyDocument[];
    categories: DocumentUploadCategory[];
    isLoading: boolean;
    spot: SpotInterface;
    uploadDocuments: (spotUuid: string, files: File[], category: DocumentUploadCategory) => Promise<void>;
};

export default function OnboardingBanner({
    documents,
    categories,
    isLoading,
    spot,
    uploadDocuments,
}: OnboardingBannerProps) {
    const [missingCategories, setMissingCategories] = useState<DocumentSubCategory[]>([]);

    // Modal refs
    const uploadDocumentModalRef = useRef<ModalRef>(null);
    const entryExitModalRef = useRef<ModalRef>(null);
    const blueprintModalRef = useRef<ModalRef>(null);

    const { historicalFinancial, monthlyRentRoll, mapAndBluePrint, entryExitPhotos } = useMemo(
        () => getDocumentCategories(categories),
        [categories]
    );

    // Information that needs to update on document change
    const { documentsByCategory, totalDocumentCategories, completedCount } = useMemo(() => {
        const documentsByCategory = getDocumentsByCategory(documents);
        const documentsByCategoryValues = Object.values(documentsByCategory);
        const totalDocumentCategories = documentsByCategoryValues.length;
        const completedCount = documentsByCategoryValues.reduce(
            (acc: number, curr: PropertyDocument[]) => acc + (curr.length > 0 ? 1 : 0),
            0
        );

        return { documentsByCategory, totalDocumentCategories, completedCount };
    }, [documents]);

    // Information that needs to update only when the banner is rendered and when the modal is closed
    function updateMissingCategories() {
        const newMissingCategories = Object.keys(documentsByCategory).filter(
            (category) =>
                documentsByCategory[category as DocumentSubCategory].length === 0 && category !== DocumentSubCategory.W9
        );
        setMissingCategories(newMissingCategories as DocumentSubCategory[]);
    }

    function openUploadDocumentModal() {
        trackEvent('Onboarding Banner Clicked', { spotName: spot.name, spot: spot.pk });
        uploadDocumentModalRef.current?.openModal();
    }

    useEffect(() => {
        updateMissingCategories();
    }, []);

    const uploadNewDocuments = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>, category: DocumentUploadCategory) => {
            if (e.target.files?.length) {
                await uploadDocuments(spot.uuid, Array.from(e.target.files), category);
                e.target.files = null;
                trackEvent('Onboarding Banner Uploaded', {
                    spotName: spot.name,
                    spot: spot.pk,
                    type: category.sub_category ? SUBCATEGORY_LABELS[category.sub_category] : category.category,
                });
            }
        },
        [uploadDocuments, spot]
    );

    const hasMissingCategories = missingCategories.length > 0;

    useEffect(() => {
        if (hasMissingCategories) trackEvent('Onboarding Banner Viewed', { spotName: spot.name, spot: spot.pk });
    }, []);

    if (!hasMissingCategories) return null;

    return (
        <>
            <div className={styles.banner}>
                <FolderIcon className={styles.itemIcon} />

                <div>
                    <div className={styles.bannerHeader}>
                        <span className={styles.bannerTitle}>Finish setting up your property</span>
                        <span className={styles.badge}>
                            {completedCount} of {totalDocumentCategories} done
                        </span>
                    </div>
                    <p className={styles.bannerDescription}>
                        Add your maps and entry/exit photos so we can place signage and go live faster.
                    </p>
                </div>

                <Button color="primary" size="sm" className={styles.bannerButton} onClick={openUploadDocumentModal}>
                    Continue Setup →
                </Button>
            </div>

            {/* Upload Document Modal */}
            <Modal
                ref={uploadDocumentModalRef}
                title="Finish setting up your property"
                onClose={updateMissingCategories}
            >
                <div className={`${styles.itemsList} ${isLoading ? styles.loading : ''}`}>
                    <SecurityInfo />
                    {missingCategories.includes(DocumentSubCategory.REVENUE_REPORT) && (
                        <HistoricalFinancialItem
                            category={historicalFinancial}
                            historicalFinancialDocuments={documentsByCategory[DocumentSubCategory.REVENUE_REPORT]}
                            uploadDocuments={uploadNewDocuments}
                            disableInteractions={isLoading}
                        />
                    )}
                    {missingCategories.includes(DocumentSubCategory.MONTHLY_RENT_ROLL) && (
                        <MonthlyRentRollItem
                            category={monthlyRentRoll}
                            monthlyRentRollDocuments={documentsByCategory[DocumentSubCategory.MONTHLY_RENT_ROLL]}
                            uploadDocuments={uploadNewDocuments}
                            disableInteractions={isLoading}
                        />
                    )}
                    {missingCategories.includes(DocumentSubCategory.PROPERTY_MAP) && (
                        <MapAndBluePrintItem
                            category={mapAndBluePrint}
                            mapAndBluePrintDocuments={documentsByCategory[DocumentSubCategory.PROPERTY_MAP]}
                            uploadDocuments={uploadNewDocuments}
                            infoRef={blueprintModalRef}
                            disableInteractions={isLoading}
                        />
                    )}
                    {missingCategories.includes(DocumentSubCategory.PHOTO) && (
                        <EntryExitPhotosItem
                            category={entryExitPhotos}
                            entryExitPhotosDocuments={documentsByCategory[DocumentSubCategory.PHOTO]}
                            uploadDocuments={uploadNewDocuments}
                            infoRef={entryExitModalRef}
                            disableInteractions={isLoading}
                        />
                    )}

                    <Button
                        color="primary"
                        size="sm"
                        className={styles.saveButton}
                        fitContent
                        onClick={() => uploadDocumentModalRef.current?.closeModal()}
                    >
                        Save
                    </Button>
                </div>
            </Modal>

            {/* Info Modals */}
            <EntryExitInfo ref={entryExitModalRef} />
            <BlueprintInfo ref={blueprintModalRef} />
        </>
    );
}
